import imageCompression from 'browser-image-compression';

export interface CompressionResult {
  compressedFile: File;
  thumbnail: File;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
}

export async function compressImage(
  file: File,
  onProgress?: (progress: number) => void
): Promise<CompressionResult> {
  const originalSize = file.size;

  try {
    // Compress main image
    const compressionOptions = {
      maxSizeMB: 2,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
      fileType: 'image/webp',
      initialQuality: 0.85,
      preserveExif: true,
      onProgress: (progress: number) => {
        onProgress?.(progress * 0.7); // Reserve 30% for thumbnail
      }
    };

    let compressedFile: File;
    try {
      compressedFile = await imageCompression(file, compressionOptions);
    } catch (webpError) {
      // Fallback to JPEG if WebP fails
      const jpegOptions = { ...compressionOptions, fileType: 'image/jpeg' };
      compressedFile = await imageCompression(file, jpegOptions);
    }

    onProgress?.(70);

    // Generate thumbnail
    const thumbnailOptions = {
      maxSizeMB: 0.1,
      maxWidthOrHeight: 400,
      useWebWorker: true,
      fileType: 'image/webp',
      initialQuality: 0.8
    };

    const thumbnail = await imageCompression(file, thumbnailOptions);

    onProgress?.(100);

    const compressedSize = compressedFile.size;
    const compressionRatio = (originalSize - compressedSize) / originalSize;

    return {
      compressedFile,
      thumbnail,
      originalSize,
      compressedSize,
      compressionRatio
    };
  } catch (error) {
    console.error('[imageCompression] Compression failed:', error);

    // Fallback: return original file as both compressed and thumbnail
    const dummyThumbnail = new File([file], `thumb-${file.name}`, { type: file.type });
    return {
      compressedFile: file,
      thumbnail: dummyThumbnail,
      originalSize,
      compressedSize: originalSize,
      compressionRatio: 0
    };
  }
}

// Web-renderable image types that every browser can display without conversion.
const WEB_SAFE_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
// Skip compression for already-small web-safe images — not worth the decode.
const COMPRESS_SKIP_BELOW_BYTES = 1.5 * 1024 * 1024; // 1.5MB

/**
 * Compress a single image for upload. Deliberately lighter than `compressImage`:
 * it does ONE encode (no separate thumbnail pass) and skips already-small web-safe
 * images, which keeps peak memory low enough to process a bulk selection on mobile
 * Safari without the tab being killed for memory.
 *
 * Always resolves to a File — on any failure it returns the original so callers
 * never have to special-case errors. Non-web-safe inputs (e.g. iOS HEIC) are always
 * converted so they render for every visitor, regardless of size.
 */
export async function compressForUpload(file: File): Promise<File> {
  if (!file.type.startsWith('image/')) return file;

  if (WEB_SAFE_IMAGE_TYPES.includes(file.type) && file.size <= COMPRESS_SKIP_BELOW_BYTES) {
    return file;
  }

  const options = {
    maxSizeMB: 2,
    maxWidthOrHeight: 1920,
    useWebWorker: true,
    fileType: 'image/webp',
    initialQuality: 0.85,
    preserveExif: true,
  };

  try {
    return await imageCompression(file, options);
  } catch {
    try {
      // Some browsers can't encode WebP — fall back to JPEG.
      return await imageCompression(file, { ...options, fileType: 'image/jpeg' });
    } catch (error) {
      console.error('[imageCompression] compressForUpload failed, using original:', error);
      return file;
    }
  }
}
